import { Link, useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Trophy, Flame, Star, Target, Zap, Award, Crown,
  BookOpen, Calendar, Medal, FileCheck, Lock, ChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useUserPoints } from '@/hooks/gamification/useUserPoints';
import { useUserStreak } from '@/hooks/gamification/useUserStreak';
import { useUserAchievements } from '@/hooks/gamification/useUserAchievements';
import { getLevelInfo } from '@/lib/gamification/levels';

const ICON_MAP: Record<string, React.ReactNode> = {
  Trophy: <Trophy className="h-5 w-5" />,
  BookOpen: <BookOpen className="h-5 w-5" />,
  Zap: <Zap className="h-5 w-5" />,
  Award: <Award className="h-5 w-5" />,
  Crown: <Crown className="h-5 w-5" />,
  Target: <Target className="h-5 w-5" />,
  Flame: <Flame className="h-5 w-5" />,
  Calendar: <Calendar className="h-5 w-5" />,
  CalendarCheck: <Calendar className="h-5 w-5" />,
  FileCheck: <FileCheck className="h-5 w-5" />,
  Medal: <Medal className="h-5 w-5" />,
  Star: <Star className="h-5 w-5" />,
};

export function LevelProgress() {
  const { data: points } = useUserPoints();

  if (!points) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Seu Nível</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <Crown className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1">
              <p className="font-semibold">Nível 1 - Iniciante</p>
              <p className="text-xs text-muted-foreground">0 pontos</p>
              <Progress value={0} className="mt-2 h-2" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const info = getLevelInfo(points.current_level, points.total_points);

  return (
    <Card className="h-[130px] md:min-h-[160px] md:h-full flex flex-col overflow-hidden border-l-4 border-l-primary bg-gradient-to-r from-primary/5 to-transparent transition-all duration-200 hover:scale-[1.02] hover:shadow-lg hover:-translate-y-1">
      <CardHeader className="pb-1 p-3 md:p-6 md:pb-1">
        <CardTitle className="text-xs md:text-sm font-medium">Seu Nível</CardTitle>
      </CardHeader>
      <CardContent className="flex-1 flex items-center justify-center pt-0 p-3 md:p-6 md:pt-0">
        <div className="flex items-center gap-2 md:gap-3">
          <div className="flex h-10 w-10 md:h-12 md:w-12 items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex-shrink-0">
            <Crown className="h-5 w-5 md:h-6 md:w-6 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1 md:gap-2 flex-wrap">
              <p className="text-xs md:text-sm font-semibold truncate">Nível {info.level}</p>
              <Badge variant="secondary" className="text-[10px] md:text-xs px-1 md:px-2">{points.total_points} pts</Badge>
            </div>
            <p className="text-[10px] md:text-xs text-muted-foreground truncate">
              {info.pointsToNext} pts p/ próximo
            </p>
            <Progress value={info.progressPercent} className="mt-1 md:mt-2 h-1.5 md:h-2" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function StreakWidget() {
  const { data: streak } = useUserStreak();
  const currentStreak = streak?.current_streak || 0;
  const longestStreak = streak?.longest_streak || 0;

  return (
    <Card className="h-[130px] md:min-h-[160px] md:h-full flex flex-col overflow-hidden border-l-4 border-l-orange-500 bg-gradient-to-r from-orange-500/5 to-transparent transition-all duration-200 hover:scale-[1.02] hover:shadow-lg hover:-translate-y-1">
      <CardHeader className="pb-1 p-3 md:p-6 md:pb-1">
        <CardTitle className="text-xs md:text-sm font-medium flex items-center gap-1 md:gap-2">
          <Flame className="h-3 w-3 md:h-4 md:w-4 text-orange-500" />
          Sequência
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col justify-center pt-0 p-3 md:p-6 md:pt-0">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-2xl md:text-3xl font-bold text-orange-500">{currentStreak}</p>
            <p className="text-[10px] md:text-xs text-muted-foreground">dias</p>
          </div>
          <div className="text-right">
            <p className="text-xs md:text-sm font-medium">{longestStreak}</p>
            <p className="text-[10px] md:text-xs text-muted-foreground">recorde</p>
          </div>
        </div>
        <div className="mt-2 md:mt-3 flex gap-0.5 md:gap-1">
          {[...Array(7)].map((_, i) => (
            <div
              key={i}
              className={cn(
                'h-1.5 md:h-2 flex-1 rounded-full',
                i < currentStreak % 7 ? 'bg-orange-500' : 'bg-muted',
              )}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export function AchievementsPreview() {
  const { data } = useUserAchievements();
  const achievements = Array.isArray(data) ? data : [];

  const unlockedCount = achievements.filter((a) => a.unlocked).length;
  const recentUnlocked = achievements.filter((a) => a.unlocked).slice(0, 3);
  const nextToUnlock = achievements.filter((a) => !a.unlocked).slice(0, 3);

  const location = useLocation();
  const achievementsPath = location.pathname.startsWith('/enem') ? '/enem/achievements' : '/achievements';

  return (
    <Card className="h-[260px] sm:h-[220px] flex flex-col">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Conquistas</CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline">{unlockedCount}/{achievements.length}</Badge>
            <Link
              to={achievementsPath}
              className="text-xs text-primary hover:underline inline-flex items-center"
            >
              Ver todas <ChevronRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
        <CardDescription>Continue estudando para desbloquear mais</CardDescription>
      </CardHeader>
      <CardContent className="flex-1 pt-0 overflow-y-auto pr-1">
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
          {recentUnlocked.map((a) => (
            <Tooltip key={a.id}>
              <TooltipTrigger asChild>
                <div className="flex flex-col items-center gap-1">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                    {ICON_MAP[a.icon] || <Trophy className="h-5 w-5" />}
                  </div>
                  <span className="text-[10px] text-center truncate max-w-full">{a.name}</span>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p className="font-medium">{a.name}</p>
                <p className="text-xs text-muted-foreground">{a.description}</p>
                <p className="text-xs text-primary">+{a.points_reward} pontos</p>
              </TooltipContent>
            </Tooltip>
          ))}
          {nextToUnlock.map((a) => (
            <Tooltip key={a.id}>
              <TooltipTrigger asChild>
                <div className="flex flex-col items-center gap-1 opacity-50">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
                    <Lock className="h-5 w-5" />
                  </div>
                  <span className="text-[10px] text-center truncate max-w-full">???</span>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p className="font-medium">{a.name}</p>
                <p className="text-xs text-muted-foreground">{a.description}</p>
                <p className="text-xs text-primary">+{a.points_reward} pontos</p>
              </TooltipContent>
            </Tooltip>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export function WeeklyPointsCard() {
  const { data: points } = useUserPoints();

  return (
    <Card className="h-[220px] flex flex-col">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">Pontos da Semana</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-2">
          <Star className="h-5 w-5 text-yellow-500" />
          <span className="text-2xl font-bold">{points?.weekly_points || 0}</span>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          {points?.monthly_points || 0} pontos este mês
        </p>
      </CardContent>
    </Card>
  );
}
